/**
 * Imperative three.js WebGPU scene lifecycle for `ModelViewer.tsx` — kept
 * out of the Solid component so the component itself stays JSX-and-wiring
 * only (see `ModelViewer.tsx`'s doc comment for the WebGPU-only/no-fallback
 * rationale, AD-005).
 */

import * as THREE from "three/webgpu";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { createLodGeometry, type BakedModelLodBody, type BakedModelSpriteSheet } from "../three/baked-model.ts";
import { MODEL_TO_WORLD_PITCH_RAD } from "../flight/world.ts";
import { disableWebGlFallback, watchDeviceLoss } from "../three/webgpu-support.ts";
import { disposeEnhancedModel } from "../three/enhanced-model.ts";
import { buildBillboards, buildLineSegments, buildSpheres } from "./model-scene-primitives.ts";

export type ModelSceneStatus = "initializing" | "ready" | "error";

export interface ModelSceneCallbacks {
  readonly onStatus: (status: ModelSceneStatus, message?: string) => void;
}

/** Everything one `setLod` call needs beyond the LOD body itself. */
export interface ModelSceneLodOptions {
  readonly boundingRadius: number;
  /** The object's highest LOD `detail` — with `boundingRadius`, the sprite pixel-to-metre scale (`model-sprites.ts`). */
  readonly maxDetail: number;
  readonly spriteSheet?: BakedModelSpriteSheet;
  /**
   * Which alternate rendering to draw (§4.5, and §4.3's effect animation):
   * the engine draws node `stateArray[group.stateChannel]` of each group,
   * so 0 is the undamaged aircraft / first frame of a burst.
   */
  readonly stateIndex: number;
}

/**
 * Which of the two models of one aircraft the pane shows: the authentic bake,
 * the optional enhanced `.glb` (docs/engineering/enhanced-models.md), or both
 * at once, moved apart along X so they can be compared without occluding each
 * other. "both" is the mode the comparison is actually for — a toggle answers
 * "does it still look like the same aeroplane" far less well than seeing the
 * two silhouettes side by side.
 */
export type ModelComparison = "authentic" | "enhanced" | "both";

export interface ModelScene {
  /** Replaces everything currently shown with `lod`'s triangles, lines, sprites and spheres, and re-frames the camera. */
  setLod(lod: BakedModelLodBody, options: ModelSceneLodOptions): void;
  /** Hands over the loaded enhanced model, or `undefined` when the selected aircraft has none. Takes ownership: the scene disposes it. */
  setEnhanced(model: THREE.Object3D | undefined): void;
  setComparison(mode: ModelComparison): void;
  setWireframe(value: boolean): void;
  dispose(): void;
}

const GRID_SIZE = 20;
const GRID_DIVISIONS = 20;
const BACKGROUND_COLOR = 0x0a0d08;
const GRID_COLOR = 0x3a331f;
const GRID_CENTER_COLOR = 0x22261b;
const FRAME_DISTANCE_FACTOR = 2.5;
/** Half the gap between the two models in "both" mode, as a multiple of the drawn width — enough air to read each silhouette. */
const COMPARISON_SEPARATION_FACTOR = 0.6;

interface SceneState {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGPURenderer;
  readonly controls: OrbitControls;
  readonly container: HTMLDivElement;
  /** Everything the baked LOD draws, parented so the comparison layout can move it as one. */
  readonly authentic: THREE.Group;
  enhanced: THREE.Object3D | undefined;
  comparison: ModelComparison;
  /** The last LOD's declared bounding radius, kept so a re-layout can frame without a new `setLod`. */
  fallbackRadius: number;
  mesh: THREE.Mesh | undefined;
  /** Everything the current LOD added to the scene — triangles, lines, spheres and billboard groups. */
  drawn: THREE.Object3D[];
  /** Billboard groups only: re-aimed at the camera every frame (`combat-visuals.ts` uses the same CPU-side trick). */
  billboards: THREE.Group[];
  /** The one texture/material a billboard sheet shares, kept for disposal. */
  spriteAssets: { texture?: THREE.Texture; material?: THREE.Material };
  disposed: boolean;
  /** Always fulfills — see `disposeScene`'s comment for why disposal has to wait on this. */
  pendingInit: Promise<void>;
}

function buildSceneObjects(container: HTMLDivElement): {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGPURenderer;
  controls: OrbitControls;
} {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(BACKGROUND_COLOR);
  scene.add(new THREE.GridHelper(GRID_SIZE, GRID_DIVISIONS, GRID_COLOR, GRID_CENTER_COLOR));

  const camera = new THREE.PerspectiveCamera(50, 1, 0.01, 10000);
  camera.position.set(4, 3, 6);

  const renderer = new THREE.WebGPURenderer({ antialias: true, reversedDepthBuffer: true });
  disableWebGlFallback(renderer);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  container.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;

  return { scene, camera, renderer, controls };
}

function resize(state: SceneState): void {
  const width = state.container.clientWidth || 1;
  const height = state.container.clientHeight || 1;
  state.renderer.setSize(width, height, false);
  state.camera.aspect = width / height;
  state.camera.updateProjectionMatrix();
}

/**
 * Kicks off `renderer.init()`, starts the render loop on success, and
 * watches for a subsequent device loss (see the inline comment). Returns a
 * promise that always fulfills — failure is reported via `callbacks.onStatus`,
 * not via rejection, so callers never need a `.catch()` of their own.
 */
function watchInit(state: SceneState, callbacks: ModelSceneCallbacks): Promise<void> {
  return state.renderer
    .init()
    .then(() => {
      if (state.disposed) return;
      resize(state);
      state.renderer.setAnimationLoop(() => {
        state.controls.update();
        for (const group of state.billboards) group.quaternion.copy(state.camera.quaternion);
        state.renderer.render(state.scene, state.camera);
      });
      callbacks.onStatus("ready");
      watchDeviceLoss(state.renderer, (info) => {
        if (state.disposed) return;
        callbacks.onStatus("error", `GPU device lost (${info.reason}): ${info.message}`);
      });
    })
    .catch((error: unknown) => {
      if (state.disposed) return;
      callbacks.onStatus("error", error instanceof Error ? error.message : String(error));
    });
}

function applyLod(state: SceneState, lod: BakedModelLodBody, options: ModelSceneLodOptions): void {
  disposeDrawn(state);

  if (lod.indices.length > 0) {
    const geometry = createLodGeometry(lod);
    // Baked models are Z-up with the nose along +Y (models-tbl.md §4.2); the
    // same -90° X convention every other scene applies puts them upright here.
    geometry.rotateX(MODEL_TO_WORLD_PITCH_RAD);
    state.mesh = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial({ vertexColors: true, wireframe: false, side: THREE.DoubleSide }));
    state.drawn.push(state.mesh);
  }

  const lines = buildLineSegments(lod, MODEL_TO_WORLD_PITCH_RAD);
  if (lines) state.drawn.push(lines);

  const inState = <T extends { readonly stateIndex: number }>(items: readonly T[] | undefined): readonly T[] =>
    (items ?? []).filter((item) => item.stateIndex === options.stateIndex);

  state.drawn.push(...buildSpheres(inState(lod.spheres), MODEL_TO_WORLD_PITCH_RAD));

  const billboards = buildBillboards(inState(lod.billboards), options.spriteSheet, options, MODEL_TO_WORLD_PITCH_RAD);
  state.billboards = [...billboards.groups];
  state.spriteAssets = { texture: billboards.texture, material: billboards.material };
  state.drawn.push(...billboards.groups);

  for (const object of state.drawn) state.authentic.add(object);
  state.fallbackRadius = options.boundingRadius;
  layout(state);
}

/**
 * Places whichever models are on show and re-frames the camera over them.
 *
 * Separation is measured off what is actually drawn rather than off the
 * declared bounding radius, because the two models are not the same size: the
 * enhanced one carries a propeller the original does not have.
 */
function layout(state: SceneState): void {
  const enhancedShown = state.enhanced !== undefined && state.comparison !== "authentic";
  state.authentic.visible = state.comparison !== "enhanced";
  if (state.enhanced) state.enhanced.visible = enhancedShown;

  state.authentic.position.setX(0);
  state.enhanced?.position.setX(0);
  if (state.comparison === "both" && state.enhanced) {
    const width = new THREE.Box3().expandByObject(state.authentic).getSize(new THREE.Vector3()).x;
    const separation = Math.max(width, state.fallbackRadius) * COMPARISON_SEPARATION_FACTOR;
    state.authentic.position.setX(-separation);
    state.enhanced.position.setX(separation);
  }
  frameCamera(state, state.fallbackRadius);
}

/**
 * Frames whatever the LOD actually drew. Uses a box over every added object
 * rather than the mesh's bounding sphere, because a sprite-only object
 * (`FLAK`, `EXPLO`) has no mesh at all and a line-only one (`TERR*` roads)
 * has no triangles — the old mesh-sphere framing left both off-camera.
 */
function frameCamera(state: SceneState, fallbackBoundingRadius: number): void {
  const box = new THREE.Box3();
  for (const object of [state.authentic, state.enhanced]) {
    if (object && object.visible) box.expandByObject(object);
  }

  const center = box.isEmpty() ? new THREE.Vector3() : box.getCenter(new THREE.Vector3());
  const measured = box.isEmpty() ? 0 : box.getBoundingSphere(new THREE.Sphere(center)).radius;
  const radius = measured > 0 ? measured : Math.max(fallbackBoundingRadius, 0.1);
  const distance = radius * FRAME_DISTANCE_FACTOR;

  state.camera.position.set(center.x + distance * 0.6, center.y + distance * 0.45, center.z + distance * 0.6);
  state.camera.near = Math.max(radius / 100, 0.001);
  state.camera.far = Math.max(distance * 20, 10);
  state.camera.updateProjectionMatrix();
  state.controls.target.copy(center);
  state.controls.update();
}

function disposeDrawn(state: SceneState): void {
  for (const object of state.drawn) {
    state.authentic.remove(object);
    object.traverse((child) => {
      if (child instanceof THREE.Mesh || child instanceof THREE.LineSegments) child.geometry.dispose();
      // The billboard material/texture are shared across a sheet's quads and
      // disposed once below, so only per-object materials are freed here.
      if (child instanceof THREE.Mesh && child.material !== state.spriteAssets.material) (child.material as THREE.Material).dispose();
      if (child instanceof THREE.LineSegments) (child.material as THREE.Material).dispose();
    });
  }
  state.spriteAssets.material?.dispose();
  state.spriteAssets.texture?.dispose();
  state.spriteAssets = {};
  state.drawn = [];
  state.billboards = [];
  state.mesh = undefined;
}

function disposeEnhanced(state: SceneState): void {
  if (!state.enhanced) return;
  state.scene.remove(state.enhanced);
  disposeEnhancedModel(state.enhanced);
  state.enhanced = undefined;
}

function disposeScene(state: SceneState): void {
  state.disposed = true;
  disposeDrawn(state);
  disposeEnhanced(state);
  state.controls.dispose();
  if (state.container.contains(state.renderer.domElement)) state.container.removeChild(state.renderer.domElement);
  // Disposing the renderer before its own init() has settled can throw an
  // unhandled "Instance dropped in popErrorScope" rejection deep inside
  // three.js's WebGPU backend — wait for init to settle, one way or the
  // other, before disposing (pendingInit always fulfills, see watchInit).
  state.pendingInit.finally(() => {
    state.renderer.setAnimationLoop(null);
    state.renderer.dispose();
  });
}

/**
 * Creates and owns one three.js WebGPU scene attached to `container`.
 * Assumes `navigator.gpu` exists (the caller feature-detects — see
 * `ModelViewer.tsx`). Fully torn down by `dispose()`; nothing here outlives
 * that call.
 */
export function createModelScene(container: HTMLDivElement, callbacks: ModelSceneCallbacks): ModelScene {
  const { scene, camera, renderer, controls } = buildSceneObjects(container);
  const authentic = new THREE.Group();
  scene.add(authentic);
  const state: SceneState = {
    scene,
    camera,
    renderer,
    controls,
    container,
    authentic,
    enhanced: undefined,
    comparison: "authentic",
    fallbackRadius: 1,
    mesh: undefined,
    drawn: [],
    billboards: [],
    spriteAssets: {},
    disposed: false,
    pendingInit: Promise.resolve(),
  };
  state.pendingInit = watchInit(state, callbacks);

  const resizeObserver = new ResizeObserver(() => resize(state));
  resizeObserver.observe(container);

  return {
    setLod: (lod, options) => applyLod(state, lod, options),
    setEnhanced: (model) => {
      disposeEnhanced(state);
      if (model) {
        state.enhanced = model;
        scene.add(model);
      }
      layout(state);
    },
    setComparison: (mode) => {
      state.comparison = mode;
      layout(state);
    },
    setWireframe: (value) => {
      if (state.mesh) (state.mesh.material as THREE.MeshBasicMaterial).wireframe = value;
    },
    dispose: () => {
      resizeObserver.disconnect();
      disposeScene(state);
    },
  };
}
